import React, { useState, useMemo, useContext, useEffect } from 'react';
import { StarIcon, HeartIcon, MapIcon, ViewListIcon } from '../components/icons';
import { useAuth } from '../context/AuthContext';
import { AppContext } from '../context/AppContext';
import { useData } from '../context/DataContext';
import { useGeolocation } from '../hooks/useGeolocation';
import RestaurantCard from '../components/RestaurantCard';
import CartIcon from '../components/CartIcon';
import RestaurantMap from '../components/RestaurantMap';

const RestaurantsScreen: React.FC = () => {
    const { view, setView } = useContext(AppContext);
    const { currentUser } = useAuth();
    const { restaurants, activeWilayas, loading } = useData();
    const { location: geoCoords, getCurrentPosition } = useGeolocation(currentUser?.uid);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedWilaya, setSelectedWilaya] = useState(view.params?.filter || currentUser?.wilaya || '');
    const [showFavorites, setShowFavorites] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

    // Fetch location when entering map mode
    useEffect(() => {
        if (viewMode === 'map' && !geoCoords) {
            getCurrentPosition();
        }
    }, [viewMode, geoCoords, getCurrentPosition]);

    useEffect(() => {
        if (view.params?.filter) {
            setSelectedWilaya(String(view.params.filter));
        } else if (view.params?.search) {
            setSearchTerm(String(view.params.search));
        }
    }, [view.params]);

    // Filtrer les restaurants selon la recherche globale
    const globalFilteredRestaurants = useMemo(() => {
        let filtered = restaurants.filter(r => r.status === 'approved');

        if (showFavorites) {
            filtered = filtered.filter(r => r.isFavorite === true);
        }

        const query = searchTerm.trim().toLowerCase();
        let searchResults = filtered;
        if (query) {
            searchResults = filtered.filter(restaurant => {
                if (!restaurant || !restaurant.name) return false;
                
                const matchesRestaurantName = restaurant.name.toLowerCase().includes(query);
                const matchesSlogan = restaurant.slogan?.toLowerCase().includes(query) || false;

                const matchesDishOrCategory = restaurant.menu?.some(c => {
                    if (!c || !c.category) return false;
                    const matchesCategory = c.category.toLowerCase().includes(query);
                    const matchesDish = c.dishes?.some(d =>
                        (d.name?.toLowerCase().includes(query) || false) ||
                        (d.description?.toLowerCase().includes(query) || false)
                    ) || false;
                    return matchesCategory || matchesDish;
                }) || false;

                return matchesRestaurantName || matchesSlogan || matchesDishOrCategory;
            });
        }

        // Sort: Online first
        return [...searchResults].sort((a, b) => {
            if (a.isOnline && !b.isOnline) return -1;
            if (!a.isOnline && b.isOnline) return 1;
            return 0;
        });
    }, [searchTerm, restaurants, showFavorites]);

    // Grouper les restaurants par Wilaya
    const restaurantsByWilaya = useMemo(() => {
        const grouped: Record<string, typeof restaurants> = {};

        // Initialiser les groupes pour l'ordre, mais seulement pour les wilayas actives
        activeWilayas.forEach(wilaya => {
            grouped[wilaya] = [];
        });

        globalFilteredRestaurants.forEach(restaurant => {
            const wilaya = restaurant.wilaya;
            if (wilaya && grouped[wilaya]) {
                grouped[wilaya].push(restaurant);
            }
        });

        return grouped;
    }, [globalFilteredRestaurants, activeWilayas]);

    const handleSelectRestaurant = (id: string) => {
        setView({ screen: 'restaurantDetail', params: { id } });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <h1 className="text-xl">Chargement...</h1>
            </div>
        );
    }

    return (
        <div className="p-4 pb-24">
            <div className="flex justify-between items-center mt-4 mb-2">
                <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic">Restaurants</h1>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                        className={`p-2 rounded-full transition-all duration-300 shadow-sm ${viewMode === 'map' ? 'bg-blue-600 text-white scale-110' : 'bg-white text-blue-600 hover:bg-blue-50'}`}
                        title={viewMode === 'list' ? "Passer en vue carte" : "Revenir en vue liste"}
                    >
                        {viewMode === 'list' ? <MapIcon className="h-6 w-6" /> : <ViewListIcon className="h-6 w-6" />}
                    </button>
                    <button
                        onClick={() => setShowFavorites(!showFavorites)}
                        className={`p-2 rounded-full transition-all duration-300 shadow-sm ${showFavorites ? 'bg-red-600 text-white scale-110' : 'bg-white text-red-600 hover:bg-red-50'}`}
                        title={showFavorites ? "Afficher tous les restaurants" : "Afficher mes favoris"}
                    >
                        <HeartIcon className={`h-6 w-6 ${showFavorites ? 'fill-current' : ''}`} />
                    </button>
                    <CartIcon />
                </div>
            </div>

            <div className="sticky top-0 bg-gray-50 py-2 z-10">
                <div className="relative mb-4">
                    <input
                        type="text"
                        placeholder="Rechercher un restaurant, un plat..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-3 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 shadow-sm text-base"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => {
                                setSearchTerm('');
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 bg-gray-100/80 hover:bg-gray-200 transition-colors rounded-full p-1"
                            title="Effacer la recherche"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    )}
                </div>

                {activeWilayas.length > 0 && currentUser?.role !== 'client' && (
                    <div className="flex overflow-x-auto space-x-3 pb-2 no-scrollbar">
                        <button onClick={() => setSelectedWilaya('')} className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-colors ${selectedWilaya === '' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                            Toutes
                        </button>
                        {activeWilayas.map((wilaya) => (
                            <button
                                key={wilaya}
                                onClick={() => setSelectedWilaya(wilaya)}
                                className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-colors ${selectedWilaya === wilaya ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            >
                                {wilaya}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-4 space-y-8">
                {viewMode === 'map' ? (
                    <RestaurantMap
                        restaurants={globalFilteredRestaurants.filter(r => !selectedWilaya || r.wilaya === selectedWilaya)}
                        onViewMenu={handleSelectRestaurant}
                        userLocation={geoCoords || currentUser?.location}
                    />
                ) : (
                    currentUser?.role === 'client' ? (
                        // Clients see a direct grid or scroll of their local restaurants without wilaya headers
                        <>
                            {globalFilteredRestaurants.filter(r => r.wilaya === currentUser.wilaya).length > 0 ? (
                                <div className="grid grid-cols-2 gap-4 animate-fade-in pr-2">
                                    {globalFilteredRestaurants
                                        .filter(r => r.wilaya === currentUser.wilaya)
                                        .map(restaurant => (
                                            <RestaurantCard
                                                key={restaurant.id}
                                                restaurant={restaurant}
                                                onClick={() => handleSelectRestaurant(restaurant.id)}
                                            />
                                        ))
                                    }
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 px-6 text-center animate-fade-in">
                                    <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mb-6 shadow-inner relative">
                                        <div className="absolute inset-0 bg-red-100 rounded-full animate-ping opacity-20"></div>
                                        <span className="text-4xl relative z-10">🍳</span>
                                    </div>
                                    <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic mb-2">Bientôt du nouveau !</h3>
                                    <p className="text-gray-500 font-bold text-xs uppercase tracking-wide leading-relaxed italic max-w-[200px]">
                                        Aucun restaurant n'est disponible dans votre ville <span className="text-red-600">({currentUser?.wilaya})</span> pour le moment.
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (
                        activeWilayas.map(wilaya => {
                            if (selectedWilaya && selectedWilaya !== wilaya) return null;
                            const wilayaRestaurants = restaurantsByWilaya[wilaya] || [];
                            if (wilayaRestaurants.length === 0) return null;

                            return (
                                <div key={wilaya} className="animate-fade-in">
                                    <div className="flex justify-between items-center mb-3 px-1">
                                        <h2 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic">{wilaya}</h2>
                                        <span className="text-[10px] font-black text-gray-400 bg-gray-100 px-2 py-0.5 rounded-lg uppercase tracking-widest">{wilayaRestaurants.length} Restos</span>
                                    </div>

                                    <div className="flex overflow-x-auto gap-4 pb-4 pt-2 pr-5 no-scrollbar">
                                        {wilayaRestaurants.map(restaurant => (
                                            <div key={restaurant.id} className="w-48 flex-shrink-0">
                                                <RestaurantCard
                                                    restaurant={restaurant}
                                                    onClick={() => handleSelectRestaurant(restaurant.id)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )
                )}

                {/* Message Aucun Résultat Global (Search/Filters) */}
                {((viewMode === 'list' && currentUser?.role !== 'client' && !activeWilayas.some(wilaya =>
                    (!selectedWilaya || selectedWilaya === wilaya) &&
                    (restaurantsByWilaya[wilaya]?.length > 0)
                )) || (viewMode === 'list' && currentUser?.role === 'client' && globalFilteredRestaurants.filter(r => r.wilaya === currentUser.wilaya).length === 0)) && (
                        <div className="flex flex-col items-center justify-center py-20 px-10 text-center animate-fade-in">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6 shadow-inner relative">
                                <span className="text-4xl grayscale opacity-50">🔍</span>
                            </div>
                            
                            <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic mb-2">
                                {showFavorites ? "Aucun favori" : "Aucun résultat"}
                            </h3>
                            
                            <p className="text-gray-500 font-bold text-xs uppercase tracking-wide leading-relaxed italic max-w-[250px] mb-8">
                                {showFavorites
                                    ? "Vous n'avez pas encore de restaurants favoris correspondant à vos filtres."
                                    : "Nous n'avons trouvé aucun restaurant correspondant à votre recherche."}
                            </p>
                            
                            <button
                                onClick={() => { setSearchTerm(''); setSelectedWilaya(''); setShowFavorites(false); }}
                                className="px-8 py-3 bg-red-600 text-white font-black rounded-xl shadow-lg shadow-red-100 hover:bg-red-700 active:scale-95 transition-all uppercase tracking-widest text-xs italic"
                            >
                                Effacer les filtres
                            </button>
                        </div>
                    )}
            </div>
        </div>
    );
};

export default RestaurantsScreen;